import { BrowserAuthErrorMessage, EventType, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { MsalConfig, LoginRequest } from "../config/authconfig";
import { enhancedSessionTracking, smartSessionTracking } from "./sessiontracking";
import { setUserContext, trackPageInit } from '../utils/analytics';
import { captureHomeDeepLink } from '../utils/homeDeepLinks';

export const msalInstance = new PublicClientApplication(MsalConfig);
captureHomeDeepLink();
const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    // Set user context after MSAL initialization
    setUserContext(accounts[0]);
    trackPageInit('/', 'landing page', 'landing')
}

// Guard to prevent infinite loops
let isProcessingEvent = false;
let isTokenRedirectInProgress = false;

type MsalErrorLike = {
    errorCode?: string;
    errorMessage?: string;
    subError?: string;
    message?: string;
};

type AcquireTokenOptions = {
    forceRefresh?: boolean;
};

const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

const getMsalError = (error: unknown): MsalErrorLike => {
    return error && typeof error === 'object' ? error as MsalErrorLike : {};
};

const getJwtExpiresAt = (token: string): number | null => {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;

        const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + (4 - normalizedPayload.length % 4) % 4, '=');
        const decodedPayload = JSON.parse(atob(paddedPayload)) as { exp?: number };
        return decodedPayload.exp ? decodedPayload.exp * 1000 : null;
    } catch {
        return null;
    }
};

const isAccessTokenUsable = (accessToken: string, expiresOn?: Date | null): boolean => {
    const expiresAt = getJwtExpiresAt(accessToken) || expiresOn?.getTime();
    return Boolean(accessToken && expiresAt && expiresAt > Date.now() + TOKEN_EXPIRY_BUFFER_MS);
};

const requiresInteractiveTokenAcquisition = (error: unknown): boolean => {
    if (error instanceof InteractionRequiredAuthError) {
        return true;
    }

    const { errorCode, errorMessage, subError, message } = getMsalError(error);

    return (
        InteractionRequiredAuthError.isInteractionRequiredError(errorCode, errorMessage || message, subError) ||
        errorCode === BrowserAuthErrorMessage.monitorIframeTimeoutError.code ||
        errorCode === 'invalid_grant'
    );
};

const startInteractiveTokenAcquisition = async (instance: any, account: any): Promise<void> => {
    if (isTokenRedirectInProgress) {
        console.log('Interactive token acquisition already in progress, skipping duplicate redirect');
        return;
    }

    isTokenRedirectInProgress = true;

    try {
        if (account) {
            await instance.acquireTokenRedirect({
                scopes: LoginRequest.scopes,
                account
            });
            return;
        }

        await instance.loginRedirect(LoginRequest);
    } catch (redirectError) {
        isTokenRedirectInProgress = false;
        console.error('Failed to start interactive token acquisition:', redirectError);
    }
};

msalInstance.addEventCallback(async (event) => {
    // Prevent infinite loops by checking if we're already processing
    if (isProcessingEvent) {
        console.log("Already processing MSAL event, skipping to prevent infinite loop");
        return;
    }

    try {
        isProcessingEvent = true;

        // @ts-ignore
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account){
            // @ts-ignore
            msalInstance.setActiveAccount(event.payload?.account);

            // Note: Initial session tracking is handled by the root component to avoid duplicates
            // No need to call session tracking here
            console.log("MSAL LOGIN_SUCCESS: Account set, session tracking handled by root component");
        }

        if (event.eventType === EventType.LOGIN_FAILURE) {
            console.log(JSON.stringify(event));
        }

        // Handle token refresh events - use smart tracking to avoid excessive session records
        if (event.eventType === EventType.SSO_SILENT_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
            // @ts-ignore
            if (event.payload?.account) {
                // @ts-ignore
                msalInstance.setActiveAccount(event.payload.account);
            }

            // Use smart session tracking for token refresh to avoid excessive records
            try {
                await smartSessionTracking();
                console.log("Smart session tracking called after token refresh");
            } catch (error) {
                console.error("Failed to track session after token refresh:", error);
            }
        }

        // Note: TOKEN_EXPIRED event doesn't exist in MSAL, tokens are handled automatically
    } finally {
        // Always reset the guard
        isProcessingEvent = false;
    }
});

export const acquireAccessToken = async (msalInstance: any, options: AcquireTokenOptions = {}) => {
    let activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();

    // Try to set an active account if we don't have one but have accounts available
    if (!activeAccount && accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        activeAccount = accounts[0];
    }

    const account = activeAccount || accounts[0];

    if (!account) {
        await startInteractiveTokenAcquisition(msalInstance, null);
        return '';
    }

    const tokenRequest = {
        scopes: LoginRequest.scopes,
        account
    };

    try {
        const authResult = await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            forceRefresh: options.forceRefresh
        });

        if (isAccessTokenUsable(authResult.accessToken, authResult.expiresOn)) {
            return authResult.accessToken;
        }

        const refreshedResult = await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            forceRefresh: true
        });

        if (isAccessTokenUsable(refreshedResult.accessToken, refreshedResult.expiresOn)) {
            return refreshedResult.accessToken;
        }

        console.warn('MSAL returned an expired or near-expired access token; redirecting to refresh authentication.');
        await startInteractiveTokenAcquisition(msalInstance, account);
        return '';
    } catch (error) {
        if (requiresInteractiveTokenAcquisition(error)) {
            console.warn('Silent token acquisition requires interaction; redirecting to refresh authentication.', error);
            await startInteractiveTokenAcquisition(msalInstance, account);
            return '';
        }

        console.error('Token acquisition failed:', error);
        return '';
    }
};

export const acquireBearerToken = async (msalInstance: any, options: AcquireTokenOptions = {}) => {
    const accessToken = await acquireAccessToken(msalInstance, options);
    return accessToken ? "Bearer " + accessToken : '';
};

export const extractUsername = () => {
    const activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount) return null;
    return activeAccount.name || activeAccount.username || null;
};
